import { COUNTRIES, PALLET_FOOTPRINTS, PALLET_LABELS, type PalletFootprintType } from "./constants";
import { CargoDimensionMapper } from "./cargoDimensions";
import { EmailBodyMarkupMapper } from "./emailBodyMarkupMapper";
import type { CargoLine, FormState, TimeMode } from "./types";

export class OptionalTextMapper {
  static readonly EMPTY = "Not provided";

  provided(raw: string): string | null {
    const value = raw.trim();
    return value ? value : null;
  }

  display(raw: string): string {
    return this.provided(raw) ?? OptionalTextMapper.EMPTY;
  }
}

export function countryName(code: string): string {
  if (!code) return "";
  const found = COUNTRIES.find((c) => c.c === code);
  return found ? found.n : code;
}

export function routeAddressLine(
  countryCode: string,
  postal: string,
  city: string,
  street = "",
  houseNumber = "",
): string {
  const streetLine = [street.trim(), houseNumber.trim()].filter(Boolean).join(" ");
  const locality = [postal.trim(), city.trim()].filter(Boolean).join(" ");
  return [countryName(countryCode.trim()), streetLine, locality].filter(Boolean).join(", ");
}

export function formatTimeRange(type: TimeMode, from: string, until: string, fixed: string): string | null {
  if (type === "fixed" && fixed) return `${fixed} (Fixed)`;
  if (type === "window" && (from || until)) return `${from || " "} to ${until || " "}`;
  return null;
}

function footprintDims(type: PalletFootprintType, separator: string): string {
  const fp = PALLET_FOOTPRINTS[type];
  return `${fp.length}${separator}${fp.width}`;
}

export function cargoDims(line: CargoLine): string {
  if (line.type === "half") {
    return " ";
  }
  if (line.sizeMode === "ldm") {
    return line.ldm.trim() ? `${line.ldm.trim()} LDM` : " ";
  }
  if (!CargoDimensionMapper.hasFootprint(line.type)) {
    return `${line.length || " "}×${line.width || " "}`;
  }
  return footprintDims(line.type, "×");
}

// The email body is rendered as HTML (preview + Graph mail), so every
// user-typed value must be escaped; only our own <strong> headers and
// Outlook-safe date highlight markup stay in the body.
function esc(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

export class CargoBreakdownFormatter {
  format(line: CargoLine): string {
    const typeLabel = PALLET_LABELS[line.type];
    const qty = esc(line.qty) || "0";
    const stack = line.stack ? "Yes" : "No";
    if (line.type === "half") {
      return `• ${CargoBreakdownFormatter.qtyPrefix(qty)} ${typeLabel}, Stackable: ${stack}`;
    }
    if (line.sizeMode === "ldm") {
      return CargoBreakdownFormatter.formatLdm(line, qty, typeLabel, stack);
    }
    return CargoBreakdownFormatter.formatDimensions(line, qty, typeLabel, stack);
  }

  private static qtyPrefix(qty: string): string {
    return `${qty} Pallet`;
  }

  private static formatLdm(
    line: CargoLine,
    qty: string,
    typeLabel: string,
    stack: string,
  ): string {
    const ldm = line.ldm.trim() ? `${esc(line.ldm.trim())} LDM` : " ";
    if (line.type === "unspecified") {
      return `• ${CargoBreakdownFormatter.qtyPrefix(qty)} ${ldm}, Stackable: ${stack}`;
    }
    return `• ${CargoBreakdownFormatter.qtyPrefix(qty)} ${typeLabel}, ${ldm}, Stackable: ${stack}`;
  }

  private static formatDimensions(
    line: CargoLine,
    qty: string,
    typeLabel: string,
    stack: string,
  ): string {
    const height = line.height.trim();
    const heightPart = height ? `, H: ${esc(height)} cm` : "";
    if (CargoDimensionMapper.hasFootprint(line.type)) {
      return `• ${CargoBreakdownFormatter.qtyPrefix(qty)} ${typeLabel}${heightPart}, Stackable: ${stack}`;
    }
    const length = line.length.trim();
    const width = line.width.trim();
    const customDims = length || width ? ` (${esc(length || " ")}x${esc(width || " ")})` : "";
    return `• ${CargoBreakdownFormatter.qtyPrefix(qty)} ${typeLabel}${customDims}${heightPart}, Stackable: ${stack}`;
  }
}

function timePhaseLine(
  type: TimeMode,
  from: string,
  until: string,
  fixed: string,
  label: string,
): string | null {
  const range = formatTimeRange(type, from, until, fixed);
  return range ? `${label} ${range}` : null;
}

export function buildSubject(form: FormState): string {
  const loadCity = form.loadCity.trim() || form.loadPostal.trim() || "Unknown";
  const unloadCity = form.unloadCity.trim() || form.unloadPostal.trim() || "Unknown";
  const company = form.company.trim() || "Unknown Company";
  return `Quote Request: ${loadCity} to ${unloadCity} - ${company}`;
}

export function generateEmailText(form: FormState): string {
  const company = esc(form.company.trim());
  const name = esc(form.contactName.trim());
  const email = esc(form.email.trim());
  const phone = esc(form.phone.trim());
  const loadStr = esc(
    routeAddressLine(
      form.loadCountry,
      form.loadPostal,
      form.loadCity,
      form.loadStreet,
      form.loadHouseNumber,
    ),
  );
  const unloadStr = esc(
    routeAddressLine(
      form.unloadCountry,
      form.unloadPostal,
      form.unloadCity,
      form.unloadStreet,
      form.unloadHouseNumber,
    ),
  );

  let out = "Dear Sir / Madam,\n\nI would like to request a quote for the following transport.\n\n";

  out += "<strong>--- ROUTE ---</strong>\n";
  const dates = new OptionalTextMapper();
  const markup = new EmailBodyMarkupMapper();
  out += `${markup.highlight(`Preferred Pickup date: ${esc(dates.display(form.pickupDate))}`)}\n`;
  if (form.unloadDate.trim()) {
    out += `${markup.highlight(`Preferred Unloading date: ${esc(dates.display(form.unloadDate))}`)}\n`;
  }
  out += `Loading: ${loadStr}\n`;
  out += `Unloading: ${unloadStr}\n\n`;

  out += "<strong>--- SHIPMENT ---</strong>\n";
  out += `Goods: ${esc(form.goods) || " "}\n`;
  out += `Weight: ${esc(form.totalWeight) || " "} kg\n`;

  const breakdown = new CargoBreakdownFormatter();
  const items: string[] = [];
  for (const line of form.cargo) {
    items.push(breakdown.format(line));
  }
  if (items.length) out += `Breakdown:\n${items.join("\n")}\n\n`;
  else out += "\n";

  const reqs: string[] = [];
  if (form.adr.active) {
    const adrBits = [
      form.adr.adrClass.trim() && `Class ${esc(form.adr.adrClass)}`,
      form.adr.un.trim() && `UN ${esc(form.adr.un)}`,
    ].filter(Boolean);
    reqs.push(`• Dangerous Goods (ADR): ${adrBits.join(", ") || "Yes"}`);
  }
  if (form.temp.active) {
    reqs.push(`• Temperature: ${esc(form.temp.min) || " "}°C to ${esc(form.temp.max) || " "}°C`);
  }
  if (form.ftl.active) {
    reqs.push("• FTL: Requested");
  }
  if (form.tail.active) {
    const l = form.tail.load;
    const u = form.tail.unload;
    reqs.push(
      `• Tail Lift: ${
        l && u ? "Loading & Unloading" : l ? "Loading only" : u ? "Unloading only" : "Requested"
      }`,
    );
  }
  if (form.time.active) {
    const tArr: string[] = [];
    const loadLine = timePhaseLine(
      form.time.loadType,
      form.time.loadFrom,
      form.time.loadUntil,
      form.time.loadFixed,
      "Load",
    );
    const unloadLine = timePhaseLine(
      form.time.unloadType,
      form.time.unloadFrom,
      form.time.unloadUntil,
      form.time.unloadFixed,
      "Unload",
    );
    if (loadLine) tArr.push(esc(loadLine));
    if (unloadLine) tArr.push(esc(unloadLine));
    if (form.time.deliveryDate) {
      tArr.push(markup.highlight(`Preferred Delivery deadline: ${esc(form.time.deliveryDate)}`));
    }
    reqs.push(`• Time Windows: ${tArr.length ? tArr.join(", ") : "Requested"}`);
  }
  if (form.ref.active && form.ref.reference) {
    const label =
      (form.ref.loadReference || "").trim() || (form.ref.unloadReference || "").trim()
        ? "Invoice reference"
        : "Reference";
    reqs.push(`• ${label}: ${esc(form.ref.reference)}`);
  }
  if (form.ref.active && form.ref.loadReference) {
    reqs.push(`• Loading reference: ${esc(form.ref.loadReference)}`);
  }
  if (form.ref.active && form.ref.unloadReference) {
    reqs.push(`• Unloading reference: ${esc(form.ref.unloadReference)}`);
  }
  if (form.ref.active && form.ref.notes) reqs.push(`• Notes: ${esc(form.ref.notes)}`);
  if (form.attachments.active) {
    const names = form.attachments.files.map((file) => esc(file.name)).filter(Boolean);
    reqs.push(`• Additional attachments: ${names.length ? names.join(", ") : "Requested"}`);
  }

  if (reqs.length) out += `<strong>--- ADDITIONAL SERVICES ---</strong>\n${reqs.join("\n")}\n\n`;

  if (form.comment.trim()) out += `\n${esc(form.comment.trim())}\n`;

  out += `Looking forward to your reply.\n\nKind regards,\n${name}\n${company}\n${email}${
    phone ? `\n${phone}` : ""
  }`;
  return out;
}
